import moment, { type Moment } from 'moment';

import * as graphql from '@treams/treams-network';

/**
 * A placement counts as outdated when nobody reviewed it for the template's
 * `outdatedAfterDays`. Saving a motivation writes a new placement, so a review
 * without a move also resets the clock.
 * @param placement the placement to check
 * @param outdatedAfterDays the template's threshold in days
 * @param now reference time, injectable for tests
 */
export function isOutdated(
	placement: Pick<graphql.TalentReviewGridPlacement, 'createdAt'>,
	outdatedAfterDays: number,
	now: Moment = moment(),
) {
	return now.diff(moment(placement.createdAt), 'days') >= outdatedAfterDays;
}

/**
 * Whether this placement sits in a different cell than the definitive
 * placement of the last earlier day.
 * @param placement the placement to check
 */
export function movedFromPrevious(
	placement: Pick<graphql.TalentReviewGridPlacement, 'row' | 'column' | 'previous'>,
) {
	const { previous } = placement;
	return !!previous && (previous.row !== placement.row || previous.column !== placement.column);
}

/** Statuses for the closed sidebar row: the list is a work queue, not a log. */
export type SidebarRowStatus =
	| 'noMotivation'
	| 'noScore'
	| 'noMotivationAndScore'
	| 'moved'
	| 'outdated'
	| 'reviewed';

const missingReviewStatus = (
	placement: Pick<graphql.TalentReviewGridPlacement, 'motivation' | 'score' | 'localDate'>,
	options: { hasScores: boolean; localDate: string },
): 'noMotivation' | 'noScore' | 'noMotivationAndScore' | undefined => {
	if (placement.localDate !== options.localDate) {
		return undefined;
	}

	const noMotivation = !placement.motivation?.trim();
	const noScore = options.hasScores && !placement.score;
	if (noMotivation && noScore) {
		return 'noMotivationAndScore';
	}

	if (noMotivation) {
		return 'noMotivation';
	}

	if (noScore) {
		return 'noScore';
	}

	return undefined;
};

/**
 * Whether this placement still needs motivation and/or score on the viewed
 * day. Older empty fields are not today's work.
 * @param placement the person's placement
 * @param options viewed-day context
 * @param options.hasScores whether the workspace scores people
 * @param options.localDate the day shown in the sidebar
 */
export function isIncompleteOnViewedDay(
	placement: Pick<graphql.TalentReviewGridPlacement, 'motivation' | 'score' | 'localDate'>,
	options: { hasScores: boolean; localDate: string },
) {
	return !!missingReviewStatus(placement, options);
}

/**
 * Drop a person from the frozen incomplete group once their card is left and
 * the review is complete, so the row can leave the work queue. No-op while
 * they still need a field, or if they were never in the set.
 * @param incompleteUserIDs the frozen incomplete group
 * @param userID the person whose card was just left
 * @param stillIncomplete whether they still need motivation and/or score
 */
export function releaseIncompleteIfComplete(
	incompleteUserIDs: Set<string>,
	userID: string,
	stillIncomplete: boolean,
) {
	if (stillIncomplete || !incompleteUserIDs.has(userID)) {
		return incompleteUserIDs;
	}

	const next = new Set(incompleteUserIDs);
	next.delete(userID);
	return next;
}

/**
 * What a closed sidebar row should say. Missing motivation and/or score on the
 * viewed day wins (the work to do). A session move is only shown once those
 * fields are filled; older empty fields fall through to outdated or last review.
 * @param placement the person's placement
 * @param options row context
 * @param options.outdated past the template's threshold
 * @param options.movedThisSession moved during this page session
 * @param options.hasScores whether the workspace scores people
 * @param options.localDate the day shown in the sidebar
 */
export function sidebarRowStatuses(
	placement: Pick<graphql.TalentReviewGridPlacement, 'motivation' | 'score' | 'localDate'>,
	options: {
		outdated: boolean;
		movedThisSession: boolean;
		hasScores?: boolean;
		localDate: string;
	},
): SidebarRowStatus[] {
	const missing = missingReviewStatus(placement, {
		hasScores: !!options.hasScores,
		localDate: options.localDate,
	});
	if (missing) {
		return [missing];
	}

	if (options.movedThisSession) {
		return ['moved'];
	}

	if (options.outdated) {
		return ['outdated'];
	}

	return ['reviewed'];
}

/** One sidebar row: a placed team member. People not on the grid are not listed. */
export type SidebarEntry = {
	user: graphql.User;
	placement: graphql.TalentReviewGridPlacement;
};

const fullName = (entry: SidebarEntry) =>
	`${entry.user.firstName} ${entry.user.lastName}`.toLowerCase();

const byName = (a: SidebarEntry, b: SidebarEntry) => fullName(a).localeCompare(fullName(b));

const byOldestReview = (a: SidebarEntry, b: SidebarEntry) =>
	new Date(a.placement.createdAt).getTime() - new Date(b.placement.createdAt).getTime() ||
	byName(a, b);

/** Work-queue groups, in list order. */
export type SidebarWorkGroup = 'incomplete' | 'outdated' | 'reviewed' | 'moved';

/** Input that splits the sidebar into work-queue groups. */
export type SidebarSortOptions = {
	incompleteUserIDs?: Set<string>;
	outdatedUserIDs: Set<string>;
	movedUserIDs?: readonly string[];
	holdUserID?: string;
};

const isMovedEntry = (entry: SidebarEntry, options: SidebarSortOptions) => {
	if (entry.user.id === options.holdUserID) {
		return false;
	}

	return !!options.movedUserIDs?.includes(entry.user.id);
};

/**
 * Which work-queue bucket a row belongs to. Incomplete wins, then a completed
 * session move, then outdated, then last review.
 * @param entry the sidebar row
 * @param options the same grouping input as `sortSidebarEntries`
 */
export function sidebarWorkGroup(
	entry: SidebarEntry,
	options: SidebarSortOptions,
): SidebarWorkGroup {
	if (options.incompleteUserIDs?.has(entry.user.id)) {
		return 'incomplete';
	}

	if (isMovedEntry(entry, options)) {
		return 'moved';
	}

	if (options.outdatedUserIDs.has(entry.user.id)) {
		return 'outdated';
	}

	return 'reviewed';
}

/**
 * Who "next" should open. Walks the list as shown, except the last last-review
 * jumps to remaining missing work instead of into this session's move dump. No
 * wrap when that work is empty: the card closes.
 * @param ordered the rows in sidebar order (already filtered if searching)
 * @param selectedUserID the open card
 * @param options the same grouping input as `sortSidebarEntries`
 */
export function nextSidebarWork(
	ordered: SidebarEntry[],
	selectedUserID: string,
	options: SidebarSortOptions,
) {
	const index = ordered.findIndex((entry) => entry.user.id === selectedUserID);
	if (index === -1) {
		return undefined;
	}

	const rest = ordered.filter((entry) => sidebarWorkGroup(entry, options) === 'reviewed');
	const isLastReviewed =
		sidebarWorkGroup(ordered[index], options) === 'reviewed' &&
		rest[rest.length - 1]?.user.id === selectedUserID;
	if (isLastReviewed) {
		return ordered.find((entry) => sidebarWorkGroup(entry, options) === 'incomplete');
	}

	return ordered[index + 1];
}

/**
 * Keep an open card at the list index it had when it was opened, so a
 * dropdown or drop does not shuffle the row. "Next" and close lift the pin.
 * @param ordered the naturally sorted rows
 * @param pin whose index to restore
 * @param pin.userID the open card
 * @param pin.index the index at the moment the card opened
 */
export function pinSidebarEntry(ordered: SidebarEntry[], pin?: { userID: string; index: number }) {
	if (!pin) {
		return ordered;
	}

	const at = ordered.findIndex((entry) => entry.user.id === pin.userID);
	if (at === -1 || at === pin.index) {
		return ordered;
	}

	const entry = ordered[at];
	const without = ordered.filter((item) => item.user.id !== pin.userID);
	const index = Math.max(0, Math.min(pin.index, without.length));
	return [...without.slice(0, index), entry, ...without.slice(index)];
}

/**
 * Sidebar order: today's incomplete work first, then outdated people (A–Z),
 * then last-review oldest first, then this session's completed moves with
 * the most recent at the bottom. Callers pass a frozen incomplete set so a
 * while its card is open; leaving a completed card drops them (see
 * `releaseIncompleteIfComplete`). Newly incomplete people still join first,
 * except the open card (`holdUserID`): a dropdown or drop must not move it.
 * @param placements the current placements
 * @param options grouping input
 * @param options.incompleteUserIDs users with missing motivation/score on the viewed day
 * @param options.outdatedUserIDs users to show in the outdated group
 * @param options.movedUserIDs users moved this session, oldest first
 * @param options.holdUserID the open card; keep its current group until left
 */
export function sortSidebarEntries(
	placements: graphql.TalentReviewGridPlacement[],
	options: SidebarSortOptions,
): SidebarEntry[] {
	const incompleteIDs = options.incompleteUserIDs ?? new Set();
	const movedOrder = options.movedUserIDs ?? [];
	const placed: SidebarEntry[] = placements.map((placement) => ({
		user: placement.user as graphql.User,
		placement,
	}));
	const isMoved = (entry: SidebarEntry) => isMovedEntry(entry, options);
	const byMoveRecency = (a: SidebarEntry, b: SidebarEntry) => {
		const aIndex = movedOrder.indexOf(a.user.id);
		const bIndex = movedOrder.indexOf(b.user.id);
		if (aIndex === -1 && bIndex === -1) {
			return byName(a, b);
		}

		if (aIndex === -1) {
			return -1;
		}

		if (bIndex === -1) {
			return 1;
		}

		return aIndex - bIndex;
	};

	const incomplete = placed.filter((entry) => incompleteIDs.has(entry.user.id)).sort(byName);
	const outdated = placed
		.filter(
			(entry) =>
				!incompleteIDs.has(entry.user.id) &&
				options.outdatedUserIDs.has(entry.user.id) &&
				!isMoved(entry),
		)
		.sort(byName);
	const moved = placed
		.filter((entry) => !incompleteIDs.has(entry.user.id) && isMoved(entry))
		.sort(byMoveRecency);
	const rest = placed
		.filter(
			(entry) =>
				!incompleteIDs.has(entry.user.id) &&
				!options.outdatedUserIDs.has(entry.user.id) &&
				!isMoved(entry),
		)
		.sort(byOldestReview);

	return [...incomplete, ...outdated, ...rest, ...moved];
}
